import { useState } from 'react';
import { FiBell, FiCheck, FiX } from 'react-icons/fi';
import { LuBookmarkPlus } from 'react-icons/lu';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useCreateEventInterest } from '../../api/events';
import { extractApiError } from '../../utils/apiError';
import { toast } from '../../utils/toast';
import { cn } from '../../utils/cn';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface EventInterestCardProps {
  eventId: number;
  eventTitle: string;
  imageUrl: string | null;
  /** Signed-in users get a prefilled form and a different consent message. */
  isAuthenticated?: boolean;
  /** Prefills the form for signed-in users. */
  defaultName?: string;
  defaultEmail?: string;
  /** Event's `interested` flag or marked this session — hides the card (the thanks modal still shows). */
  isInterested: boolean;
  onInterested: () => void;
}

/** "Not ready to buy yet?" card shown beneath the checkout button. */
export function EventInterestCard({
  eventId,
  eventTitle,
  imageUrl,
  isAuthenticated = false,
  defaultName,
  defaultEmail,
  isInterested,
  onInterested,
}: EventInterestCardProps) {
  const [formOpen, setFormOpen] = useState(false);
  const [thanksOpen, setThanksOpen] = useState(false);
  const mutation = useCreateEventInterest(eventId);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const getToken = async (): Promise<string | null> => {
    if (!executeRecaptcha) {
      toast.error('reCAPTCHA is not ready yet. Please try again in a moment.');
      return null;
    }
    try {
      return await executeRecaptcha('event_interest');
    } catch {
      toast.error('reCAPTCHA verification failed. Please try again.');
      return null;
    }
  };

  const submit = async (details: { full_name: string; email: string }) => {
    const recaptcha_token = await getToken();
    if (!recaptcha_token) return;
    mutation.mutate(
      { ...details, recaptcha_token },
      {
        onSuccess: () => {
          onInterested();
          setFormOpen(false);
          setThanksOpen(true);
        },
        onError: (err) => toast.error(extractApiError(err, 'Could not save your interest')),
      },
    );
  };

  return (
    <>
      {!isInterested && (
        <div className="rounded-3xl border border-white/8 bg-zinc-950 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 shadow-2xl shadow-black/30">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0 sm:flex-1">
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center">
              <FiBell size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-['Funnel_Display'] font-medium text-[15px]">
                Not ready to buy yet?
              </p>
              <p className="mt-1 font-['Outfit'] text-[13px] leading-snug text-zinc-400">
                Mark yourself as interested and we&apos;ll send reminders and important updates
                before the event
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            disabled={mutation.isPending}
            className="w-full sm:w-auto shrink-0 justify-center h-10 px-4 rounded-full border border-white/10 bg-zinc-900 font-['Outfit'] font-medium text-[14px] flex items-center gap-2 hover:bg-zinc-800 transition-colors cursor-pointer disabled:cursor-default disabled:hover:bg-zinc-900"
          >
            <LuBookmarkPlus size={16} />
            I&apos;m Interested
          </button>
        </div>
      )}

      {formOpen && (
        <InterestFormModal
          eventTitle={eventTitle}
          imageUrl={imageUrl}
          isAuthenticated={isAuthenticated}
          defaultName={defaultName}
          defaultEmail={defaultEmail}
          isSubmitting={mutation.isPending}
          onClose={() => setFormOpen(false)}
          onSubmit={submit}
        />
      )}
      {thanksOpen && <InterestThanksModal onClose={() => setThanksOpen(false)} />}
    </>
  );
}

function ModalShell({
  children,
  onClose,
  className = '',
}: {
  children: React.ReactNode;
  onClose: () => void;
  className?: string;
}) {
  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={cn(
          'relative w-full rounded-3xl border border-white/10 bg-black shadow-2xl',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-zinc-700 transition-colors cursor-pointer"
        >
          <FiX size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}

function InterestFormModal({
  eventTitle,
  imageUrl,
  isAuthenticated,
  defaultName = '',
  defaultEmail = '',
  isSubmitting,
  onClose,
  onSubmit,
}: {
  eventTitle: string;
  imageUrl: string | null;
  isAuthenticated: boolean;
  defaultName?: string;
  defaultEmail?: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (details: { full_name: string; email: string }) => void;
}) {
  const [fullName, setFullName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [agreed, setAgreed] = useState(false);
  const [touched, setTouched] = useState(false);

  const nameOk = fullName.trim().length > 0;
  const emailOk = EMAIL_RE.test(email.trim());
  const canSubmit = nameOk && emailOk && agreed && !isSubmitting;

  const fieldClass =
    "mt-1.5 w-full h-[50px] rounded-[17.3px] border border-[#FFFFFF12] bg-[#0D0D0D] p-4 backdrop-blur-[28px] font-['DM_Sans'] text-[14px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30";

  return (
    <ModalShell
      onClose={onClose}
      className="max-w-[688px] sm:min-h-[351px] max-h-[calc(100dvh-2rem)] overflow-y-auto py-6 pl-6 rounded-2xl border-[#262626] shadow-[0_8px_32px_0_#00000073]"
    >
      <form
        className="flex items-stretch gap-6 sm:min-h-[303px]"
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (canSubmit) onSubmit({ full_name: fullName.trim(), email: email.trim() });
        }}
      >
        {imageUrl && (
          <div className="relative hidden sm:block w-[40%] shrink-0 overflow-hidden rounded-lg">
            <img
              src={imageUrl}
              alt={eventTitle}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        )}
        <div className="min-w-0 flex-1 pr-6">
          <h2 className="pr-10 font-['Funnel_Display'] font-semibold text-[20px] leading-none text-[#EAF2FD]">
            Get notified about this event
          </h2>

          <label className="mt-4 block font-['DM_Sans'] font-medium text-[13px] leading-[19.5px] text-[#C4C4C4]">
            Full name
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Alex Morgan"
              maxLength={255}
              className={fieldClass}
            />
          </label>
          {touched && !nameOk && <p className="mt-1 text-xs text-red-400">Enter your full name</p>}

          <label className="mt-3 block font-['DM_Sans'] font-medium text-[13px] leading-[19.5px] text-[#C4C4C4]">
            Email address
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@mail.com"
              className={fieldClass}
            />
          </label>
          {touched && !emailOk && (
            <p className="mt-1 text-xs text-red-400">Enter a valid email address</p>
          )}

          <label className="mt-3 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-white cursor-pointer"
            />
            <span className="font-['Inter'] font-medium italic text-[12px] leading-5 text-[#B3B3B3]">
              {isAuthenticated
                ? 'Your email may be shared with the event organizer for event updates and marketing. '
                : 'No account needed. Your email may be used by the event organizer for event updates and marketing. '}
              You can opt out at any time. See Musicade&apos;s{' '}
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-white hover:text-zinc-300"
              >
                Privacy Policy
              </a>{' '}
              for details.
            </span>
          </label>

          <button
            type="submit"
            disabled={!canSubmit}
            className="mt-4 w-full h-[39px] rounded-full bg-white px-7 py-3 text-black font-['Outfit'] font-semibold text-[12px] leading-none hover:bg-zinc-200 transition-colors cursor-pointer disabled:opacity-50 disabled:hover:bg-white disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting…' : "I'm Interested"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function InterestThanksModal({ onClose }: { onClose: () => void }) {
  return (
    <ModalShell onClose={onClose} className="max-w-[440px] p-8 text-center">
      <div className="mx-auto mt-6 w-24 h-24 rounded-full border border-green-900/60 bg-green-950/40 flex items-center justify-center">
        <div className="w-14 h-14 rounded-full bg-green-400 flex items-center justify-center">
          <FiCheck size={28} className="text-black" strokeWidth={3} />
        </div>
      </div>
      <h2 className="mt-6 font-['Funnel_Display'] font-medium text-[24px]">
        Thanks for your interest!
      </h2>
      <p className="mt-3 font-['Outfit'] text-[14px] text-zinc-300">
        We&apos;ll keep you informed about this event and let you know if anything changes
      </p>
      <button
        type="button"
        onClick={onClose}
        className="mt-6 w-full h-12 rounded-full bg-white text-[#212121] font-['Outfit'] font-semibold text-[14px] hover:bg-zinc-200 transition-colors cursor-pointer"
      >
        Done
      </button>
    </ModalShell>
  );
}
